import { EventEmitter, Inject, Injectable, Injector, Optional, ComponentFactoryResolver, ComponentRef, EmbeddedViewRef, ApplicationRef } from '@angular/core';
import { Observable, of, Subscription } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { WorkingComponent } from '../component/working/working.component';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter, map, tap } from 'rxjs/operators';


export interface WorkingOperation {
  uiBlocking: boolean; // block ui
  container?: HTMLElement; // parent element
}

interface WorkingOperationInstance extends WorkingOperation {
  id: string;
  componentRef: ComponentRef<WorkingComponent>
}

export interface WorkingOperationEvent {
  type: 'added' | 'removed',
  operation: WorkingOperationInstance
}

@Injectable({
  providedIn: 'root'
})
export class WorkingService {

  event: EventEmitter<WorkingOperationEvent> = new EventEmitter();

  private list: Dictionary<WorkingOperationInstance> = {};

  private baseUrl: string;

  constructor(
    @Optional() @Inject(DOCUMENT) private document: Document,
    private appRef: ApplicationRef,
    private injector: Injector,
    private componentFactoryResolver: ComponentFactoryResolver,
    private router: Router,
  ) {
    const stripToBaseUrl = /[?#]/;

    this.baseUrl = this.router.url.split(stripToBaseUrl)[0];

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map((event: NavigationEnd) => event.url.split(stripToBaseUrl)[0]))
      .subscribe(baseUrl => {
        if (this.baseUrl !== baseUrl) {
          this.removeAll();
        }
        this.baseUrl = baseUrl;
      });
  }

  public get(id: string): WorkingOperation | undefined {
    return this.list[id];
  }

  public add(operation: WorkingOperation, id: string = `${Date.now()}`): void {
    // clear duplicates
    this.clear(id);
    // default to body as container if none set
    operation.container = operation.container || this.document.body;
    // create component ref and attach to container
    const componentRef = this.attach(operation.container);
    const instance = componentRef.instance;
    instance.id = id;
    instance.blockUI = !!operation.uiBlocking;
    // add operation to dictionary
    this.list[id] = {
      ...operation,
      id,
      componentRef
    };
    // emit working opearion event
    this.event.emit({
      type: 'added',
      operation: this.list[id]
    });
  }

  public remove(id: string): void {
    //
    this.clear(id);
  }

  public removeAll(): void {
    for (const id of Object.keys(this.list)) {
      this.remove(id);
    }
  }

  private clear(id: string): void {
    // get operation from dictionary
    const operation = this.list[id];
    //
    if (operation
      && operation.componentRef) {
      // remove from dom
      this.detach(operation.componentRef);
      // emit working opearion event
      this.event.emit({
        type: 'removed',
        operation
      });
      // delete operation from dictinary 
      delete this.list[id];
    }
  }

  /**
   * append component to body
   */
  private attach(container: HTMLElement | Document = this.document || document): ComponentRef<WorkingComponent> {
    // Create a component reference from the component
    const componentRef = this.componentFactoryResolver
      .resolveComponentFactory(WorkingComponent)
      .create(this.injector);
    // Attach component to the appRef so that it's inside the ng component tree
    this.appRef.attachView(componentRef.hostView);
    // Get DOM element from component
    const domElem = (componentRef.hostView as EmbeddedViewRef<any>)
      .rootNodes[0] as HTMLElement;
    // Append DOM element to the body
    container.appendChild(domElem);

    return componentRef as ComponentRef<WorkingComponent>;
  }

  private detach(componentRef: ComponentRef<WorkingComponent>): void {
    // remove it from the component tree and from the DOM
    this.appRef.detachView(componentRef.hostView);
    componentRef.destroy();
  }
}
